import React, { useState } from 'react'
import {
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
  isEmpty,
} from 'react-admin'
import {
  Tabs,
  Tab,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  TextField,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import DownloadIcon from '@mui/icons-material/Download'

import CustomAudioField from '../../components/CustomAudioField'
import CustomAttachmentField from '../../components/CustomAttachmentField'

const ChapterTab = ({
  chapter,
  bookId,
  loading,
  onReExtractText,
  selectedVoice,
}) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [formData, setFormData] = useState({
    title: chapter?.title || '',
    orderIndex: chapter?.orderIndex || 1,
    startPage: chapter?.startPage || 1,
    endPage: chapter?.endPage || 1,
  })

  // Parse content into paragraphs and sentences structure
  const [paragraphs, setParagraphs] = useState(() => chapter?.paragraphs)
  const [selectedParagraph, setSelectedParagraph] = useState(0)
  const [selectedSentence, setSelectedSentence] = useState(0)
  const [sentences, setSentences] = useState([])
  const [sentencesLoading, setSentencesLoading] = useState(false)
  const [sentenceFormData, setSentenceFormData] = useState({
    speakRate: 0,
    content: '',
    contentWithBreaks: '',
    audioPath: '',
    manualAudioPath: '',
    status: 'processing',
  })

  // Convert time input (format: MM:SS or H:MM:SS)
  const [timeInput, setTimeInput] = useState('')
  const [convertResult, setConvertResult] = useState({
    paragraphOrder: null,
    sentenceOrder: null,
  })
  // Store sentenceOrder to set after sentences are loaded
  const [pendingSentenceOrder, setPendingSentenceOrder] = useState(null)

  // Helper function to parse time string (MM:SS or H:MM:SS) to total seconds
  const parseTimeToSeconds = timeStr => {
    if (!timeStr || !timeStr.trim())
      return { totalSeconds: 0, minutes: 0, seconds: 0 }

    // Remove all spaces
    const cleaned = timeStr.trim().replace(/\s/g, '')

    // Match format: H:MM:SS or HH:MM:SS or MM:SS or M:SS
    // Support: 0:00:00, 1:05:30, 5:30, 65:30, etc.
    const parts = cleaned.split(':')

    if (parts.length === 3) {
      // Format: H:MM:SS or HH:MM:SS
      const hours = parseInt(parts[0]) || 0
      const minutes = parseInt(parts[1]) || 0
      const seconds = parseInt(parts[2]) || 0

      // Validate: minutes and seconds should be 0-59
      if (minutes > 59 || seconds > 59) {
        return { totalSeconds: 0, minutes: 0, seconds: 0 }
      }

      const totalSeconds = hours * 3600 + minutes * 60 + seconds
      const totalMinutes = hours * 60 + minutes
      return { totalSeconds, minutes: totalMinutes, seconds }
    } else if (parts.length === 2) {
      // Format: MM:SS or M:SS
      const minutes = parseInt(parts[0]) || 0
      const seconds = parseInt(parts[1]) || 0

      // Validate: seconds should be 0-59
      if (seconds > 59) {
        return { totalSeconds: 0, minutes: 0, seconds: 0 }
      }

      const totalSeconds = minutes * 60 + seconds
      return { totalSeconds, minutes, seconds }
    } else if (parts.length === 1) {
      // Single number - assume seconds
      const num = parseInt(cleaned)
      if (!isNaN(num) && num >= 0) {
        const totalSeconds = num
        const minutes = Math.floor(totalSeconds / 60)
        const seconds = totalSeconds % 60
        return { totalSeconds, minutes, seconds }
      }
    }

    return { totalSeconds: 0, minutes: 0, seconds: 0 }
  }

  // Helper function to format seconds to H:MM:SS (preferred) or MM:SS
  const formatSecondsToTime = totalSeconds => {
    if (!totalSeconds || totalSeconds === 0) return '0:00:00'

    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    // If hours > 0, use H:MM:SS format, otherwise use MM:SS
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    } else {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    }
  }

  // Handle time input change with validation
  const handleTimeInputChange = e => {
    let value = e.target.value

    // Allow only numbers and colons
    value = value.replace(/[^\d:]/g, '')

    // Limit to reasonable format (max H:MM:SS = 9:59:59)
    // But allow user to type freely and validate on blur/submit
    setTimeInput(value)
  }

  // Format time input on blur - always format to H:MM:SS
  const handleTimeInputBlur = () => {
    if (!timeInput) return

    const { totalSeconds } = parseTimeToSeconds(timeInput)
    if (totalSeconds > 0) {
      // Always format to H:MM:SS format (e.g., 0:05:30, 1:00:00)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      const formatted = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      setTimeInput(formatted)
    } else {
      // Clear if invalid
      setTimeInput('')
    }
  }

  // Helper function to get audio path for selected voice
  const getVoiceAudioPath = (voicesArray, selectedVoice) => {
    if (!voicesArray || !selectedVoice) return null
    const voiceObj = voicesArray.find(v => v.voice === selectedVoice)
    return voiceObj?.audioPath || null
  }

  // Helper function to get voice-specific data from sentence
  const getVoiceData = (sentence, selectedVoice, field) => {
    if (!sentence?.voices || !selectedVoice) return null
    const voiceObj = sentence.voices.find(v => v.voice === selectedVoice)
    return voiceObj?.[field] || null
  }

  // Helper function to get voice-specific sentence data
  const getVoiceSentenceData = (sentence, selectedVoice) => {
    if (!sentence?.voices || !selectedVoice) {
      return {
        speakRate: sentence?.speakRate || 0,
        qcDiffResult: sentence?.qcDiffResult || [],
        qcAccuracyPercentage: sentence?.qcAccuracyPercentage || 0,
        status: sentence?.status || 'processing',
        audioPath: sentence?.audioPath || '',
      }
    }

    const voiceObj = sentence.voices.find(v => v.voice === selectedVoice)
    return {
      speakRate: voiceObj?.speakRate || sentence?.speakRate || 0,
      qcDiffResult: voiceObj?.qcDiffResult || sentence?.qcDiffResult || [],
      qcAccuracyPercentage:
        voiceObj?.qcAccuracyPercentage || sentence?.qcAccuracyPercentage || 0,
      status: voiceObj?.status || sentence?.status || 'processing',
      audioPath: voiceObj?.audioPath || sentence?.audioPath || '',
    }
  }

  // Update paragraphs when chapter prop changes
  React.useEffect(() => {
    if (chapter?.paragraphs) {
      setParagraphs(chapter.paragraphs)
      setSelectedParagraph(0) // Reset to first paragraph
      setSelectedSentence(0) // Reset to first sentence
    }
  }, [chapter?.paragraphs])

  // API function to get sentences for a specific paragraph
  const getSentences = async (bookId, chapterId, paragraphId) => {
    if (!bookId || !chapterId || paragraphId === undefined) {
      console.log('Missing required parameters for getSentences')
      return []
    }

    try {
      setSentencesLoading(true)
      const response = await dataProvider.getList('ai-book-sentences', {
        filter: { bookId, chapterId, paragraphId },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'orderIndex', order: 'ASC' },
      })
      return response.data || []
    } catch (error) {
      console.error('Error fetching sentences:', error)
      notify('Error loading sentences', { type: 'error' })
      return []
    } finally {
      setSentencesLoading(false)
    }
  }

  // Update sentence form data when selection changes
  React.useEffect(() => {
    if (sentences[selectedSentence]) {
      const sentence = sentences[selectedSentence]
      const voiceSpecificData = getVoiceSentenceData(sentence, selectedVoice)
      setSentenceFormData({
        ...sentence,
        ...voiceSpecificData,
      })
    }
  }, [selectedSentence, sentences, selectedVoice])

  // Load sentences when paragraph selection changes
  React.useEffect(() => {
    const loadSentences = async () => {
      if (
        paragraphs &&
        paragraphs[selectedParagraph] &&
        bookId &&
        chapter?.id
      ) {
        // Try different ways to get paragraphId
        const paragraph = paragraphs[selectedParagraph]
        let paragraphId =
          paragraph.orderIndex || paragraph.id || selectedParagraph

        const fetchedSentences = await getSentences(
          bookId,
          chapter.id,
          paragraphId,
        )
        setSentences(fetchedSentences)
        // Reset to first sentence initially (will be overridden by pendingSentenceOrder effect if needed)
        setSelectedSentence(0)
      }
    }
    loadSentences()
  }, [selectedParagraph, paragraphs, bookId, chapter.id])

  // Auto-select sentence when sentences are loaded and there's a pending sentenceOrder
  React.useEffect(() => {
    if (pendingSentenceOrder !== null && sentences && sentences.length > 0) {
      const sentenceIndex = sentences.findIndex(
        s => s.orderIndex === pendingSentenceOrder,
      )
      if (sentenceIndex !== -1) {
        setSelectedSentence(sentenceIndex)
      } else {
        // If not found, set to first sentence
        setSelectedSentence(0)
        notify(
          `Sentence với orderIndex ${pendingSentenceOrder} không tìm thấy`,
          { type: 'warning' },
        )
      }
      // Clear pending sentenceOrder after setting
      setPendingSentenceOrder(null)
    }
  }, [sentences, pendingSentenceOrder])

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSentenceChange = (field, value) => {
    setSentenceFormData(prev => ({ ...prev, [field]: value }))

    // Update the sentence in sentences state with voice-specific data
    setSentences(prev => {
      const updated = [...prev]
      if (updated[selectedSentence]) {
        // Initialize voices array if it doesn't exist
        if (!updated[selectedSentence].voices) {
          updated[selectedSentence].voices = []
        }

        // Find or create voice object
        let voiceObj = updated[selectedSentence].voices.find(
          v => v.voice === selectedVoice,
        )
        if (!voiceObj) {
          voiceObj = { voice: selectedVoice }
          updated[selectedSentence].voices.push(voiceObj)
        }

        // Update voice-specific field
        voiceObj[field] = value

        // Also update the main sentence field for backward compatibility
        updated[selectedSentence][field] = value
      }
      return updated
    })
  }

  // Handle reload sentence data
  const handleReloadSentence = async () => {
    try {
      const currentSentence = sentences[selectedSentence]
      if (!currentSentence) {
        notify('No sentence selected', { type: 'error' })
        return
      }

      // Fetch single sentence data using getOne with required parameters
      const response = await dataProvider.getOne('ai-book-sentences', {
        id: currentSentence.id,
        bookId: bookId,
        chapterId: chapter.id,
        paragraphId: paragraphs[selectedParagraph].orderIndex,
      })

      if (response.data) {
        // Update the sentence in the sentences array
        setSentences(prev => {
          const updated = [...prev]
          if (updated[selectedSentence]) {
            updated[selectedSentence] = {
              ...updated[selectedSentence],
              ...response.data,
            }
          }
          return updated
        })

        // Update the form data with fresh data
        setSentenceFormData({
          content: response.data.content || '',
          contentWithBreaks:
            response.data.contentWithBreaks || response.data.content || '',
          speakRate: response.data.speakRate || 0,
          qcDiffResult: response.data.qcDiffResult || [],
          qcAccuracyPercentage: response.data.qcAccuracyPercentage || '',
        })

        notify('Sentence data reloaded successfully', { type: 'success' })
      }
    } catch (error) {
      console.error('Error reloading sentence:', error)
      notify('Error reloading sentence data', { type: 'error' })
    } finally {
      setSentencesLoading(false)
    }
  }

  const handleUpdateSentenceAndTriggerTTS = async () => {
    try {
      const currentSentence = sentences[selectedSentence]
      if (!currentSentence) {
        notify('No sentence selected', { type: 'error' })
        return
      }

      // Update sentence status to processing
      handleSentenceChange('status', 'processing')

      const payload = {
        contentWithBreaks:
          sentenceFormData.contentWithBreaks || currentSentence.content,
        manualAudioPath: null,
        bookId: bookId,
        chapterId: chapter.id,
        paragraphId: paragraphs[selectedParagraph].orderIndex,
        speakRate: sentenceFormData.speakRate || 0,
        id: currentSentence.id,
        voiceId: selectedVoice,
      }

      await dataProvider.update('ai-book-sentences', {
        id: currentSentence.id,
        data: payload,
      })

      notify('Sentence updated and TTS triggered successfully', {
        type: 'success',
      })

      // Reload sentences to get updated data
      const paragraphId = paragraphs[selectedParagraph].orderIndex
      const fetchedSentences = await getSentences(
        bookId,
        chapter.id,
        paragraphId,
      )
      setSentences(fetchedSentences)

      refresh()
    } catch (error) {
      console.error('Error updating sentence and triggering TTS:', error)
      notify('Error updating sentence and triggering TTS', { type: 'error' })
      // Revert status on error
      handleSentenceChange('status', 'error')
    }
  }

  const handleGenerateParagraphAudio = async () => {
    try {
      const currentParagraph = paragraphs[selectedParagraph]
      if (!currentParagraph) {
        notify('No paragraph selected', { type: 'error' })
        return
      }

      const payload = {
        bookId: bookId,
        chapterId: chapter.id,
        paragraphId: currentParagraph.orderIndex,
        compositionType: 'paragraph',
        voiceId: selectedVoice,
      }

      await dataProvider.create('ai-books/merge-content', {
        data: payload,
      })

      notify('Paragraph audio generation started successfully', {
        type: 'success',
      })
      refresh()
    } catch (error) {
      console.error('Error generating paragraph audio:', error)
      notify('Error generating paragraph audio', { type: 'error' })
    }
  }

  const handleGenerateChapterAudio = async () => {
    try {
      const payload = {
        bookId: bookId,
        chapterId: chapter.id,
        compositionType: 'chapter',
        voiceId: selectedVoice,
      }

      await dataProvider.create('ai-books/merge-content', {
        data: payload,
      })

      notify('Chapter audio generation started successfully', {
        type: 'success',
      })
      refresh()
    } catch (error) {
      console.error('Error generating chapter audio:', error)
      notify('Error generating chapter audio', { type: 'error' })
    }
  }

  // Download chapter audio (uses chapter voice audioPath)
  // const handleDownloadChapterAudio = async () => {
  //   try {
  //     const chapterAudioPath = getVoiceAudioPath(chapter?.voices, selectedVoice)
  //     if (!chapterAudioPath) {
  //       notify('No chapter audio to download', { type: 'warning' })
  //       return
  //     }
  //     const resp = await dataProvider.create('ai-books/get-signed-url', {
  //       data: { audioPath: chapterAudioPath },
  //     })
  //     const url = resp?.data?.signedUrl
  //     if (!url) {
  //       notify('Failed to get download URL', { type: 'error' })
  //       return
  //     }
  //     notify('Downloading chapter audio...', { type: 'success' })
  //     // Direct download via signed URL (no fetch/await)
  //     const response = await fetch(url)
  //     const blob = await response.blob()
  //     const blobUrl = window.URL.createObjectURL(blob)

  //     const a = document.createElement('a')
  //     a.href = blobUrl
  //     a.style.display = 'none'

  //     const idx = parseInt(chapter?.orderIndex ?? 0, 10)
  //     const padded = String(isNaN(idx) ? 0 : idx).padStart(2, '0')
  //     const nameFromPath = `${padded}.wav`

  //     a.setAttribute('download', nameFromPath)
  //     document.body.appendChild(a)
  //     a.click()

  //     // Cleanup
  //     setTimeout(() => {
  //       document.body.removeChild(a)
  //       window.URL.revokeObjectURL(blobUrl)
  //     }, 100)
  //   } catch (e) {
  //     console.error('Error downloading chapter audio:', e)
  //     notify('Error downloading chapter audio', { type: 'error' })
  //   }
  // }

  const handleUpdateTimestamp = async () => {
    try {
      if (!bookId || !chapter?.id) {
        notify('Thiếu thông tin bookId hoặc chapterId', { type: 'error' })
        return
      }

      if (!selectedVoice) {
        notify('Vui lòng chọn voice trước khi update timestamp', {
          type: 'warning',
        })
        return
      }

      const payload = {
        bookId: bookId,
        chapterId: chapter.id,
        voiceId: selectedVoice,
      }

      await dataProvider.create('ai-books/update-timestamp', {
        data: payload,
      })

      notify('Update timestamp processing triggered successfully', {
        type: 'success',
      })
      refresh()
    } catch (error) {
      console.error('Error updating timestamp:', error)
      const errorMessage = error?.message || 'Failed to update timestamp'
      notify(errorMessage, { type: 'error' })
    }
  }

  const handleConvert = async () => {
    try {
      if (!timeInput || !timeInput.trim()) {
        notify('Vui lòng nhập thời gian (định dạng: MM:SS hoặc H:MM:SS)', {
          type: 'warning',
        })
        return
      }

      // Parse time input to total seconds
      const { totalSeconds, minutes, seconds } = parseTimeToSeconds(timeInput)

      if (totalSeconds === 0) {
        notify(
          'Thời gian không hợp lệ. Vui lòng nhập định dạng MM:SS hoặc H:MM:SS',
          {
            type: 'warning',
          },
        )
        return
      }

      console.log('Convert clicked!')
      console.log('Time input:', timeInput)
      console.log('Total seconds:', totalSeconds)
      console.log('Minutes:', minutes, 'Seconds:', seconds)
      console.log('Chapter ID:', chapter?.id)
      console.log('Book ID:', bookId)
      console.log('Selected Voice:', selectedVoice)

      // Call API to convert time to para/sentence position
      const response = await dataProvider.create('ai-books/convert-time', {
        data: {
          bookId: bookId,
          chapterId: chapter?.id,
          totalSeconds: totalSeconds,
          voiceId: selectedVoice,
        },
      })

      // Update convert result with API response
      if (response.data && response.data.data) {
        const paragraphOrder = response.data.data.paragraphOrder || null
        const sentenceOrder = response.data.data.sentenceOrder || null

        setConvertResult({
          paragraphOrder,
          sentenceOrder,
        })

        // Auto-select paragraph and sentence based on response
        if (paragraphOrder !== null && paragraphs && paragraphs.length > 0) {
          // Find paragraph index by orderIndex
          const paragraphIndex = paragraphs.findIndex(
            p => p.orderIndex === paragraphOrder,
          )

          if (paragraphIndex !== -1) {
            // Set pending sentenceOrder to be set after sentences are loaded
            if (sentenceOrder !== null) {
              setPendingSentenceOrder(sentenceOrder)
            }
            // Set selected paragraph (this will trigger useEffect to load sentences)
            setSelectedParagraph(paragraphIndex)
          } else {
            notify(
              `Paragraph với orderIndex ${paragraphOrder} không tìm thấy`,
              { type: 'warning' },
            )
          }
        }

        notify('Find successful', { type: 'success' })
      }
    } catch (error) {
      console.error('Error converting time:', error)
      const errorMessage = error?.message || 'Failed to convert time'
      notify(errorMessage, { type: 'error' })
      setConvertResult({
        paragraphOrder: null,
        sentenceOrder: null,
      })
    }
  }

  return (
    <Card>
      <CardContent>
        {/* Chapter Header Info */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={5}>
            <TextField
              label="Chapter Title"
              variant="standard"
              fullWidth
              value={formData.title || ''}
              onChange={e => handleFieldChange('title', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <TextField
              label="Order Index"
              type="number"
              variant="standard"
              fullWidth
              value={formData.orderIndex}
              onChange={e =>
                handleFieldChange('orderIndex', parseInt(e.target.value) || 1)
              }
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Chapter Status"
              variant="standard"
              fullWidth
              value={chapter?.status || 'new'}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Word Count"
              type="number"
              variant="standard"
              fullWidth
              value={chapter?.wordCount || 0}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <TextField
              label="Start Page"
              type="number"
              variant="standard"
              fullWidth
              value={formData.startPage}
              onChange={e =>
                handleFieldChange('startPage', parseInt(e.target.value) || 1)
              }
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <TextField
              label="End Page"
              type="number"
              variant="standard"
              fullWidth
              value={formData.endPage}
              onChange={e =>
                handleFieldChange('endPage', parseInt(e.target.value) || 1)
              }
              inputProps={{ min: 1 }}
            />
          </Grid>
        </Grid>

        {/* Attachment Display */}
        {chapter?.attachmentPath && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={3}>
              <CustomAttachmentField
                label="Chapter Attachment"
                attachmentPath={chapter.attachmentPath}
                loading={loading}
              />
            </Grid>
          </Grid>
        )}

        {/* Action Buttons Row */}
        {/* <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleDownloadChapterAudio}
              disabled={!getVoiceAudioPath(chapter?.voices, selectedVoice)}>
              Download Chapter {chapter?.orderIndex} Audio
            </Button>
          </Grid>
        </Grid> */}

        {/* Update Timestamp Button */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={handleUpdateTimestamp}
              disabled={loading || !selectedVoice}
              sx={{
                textTransform: 'none',
                height: '36px',
              }}>
              Update Timestamp
            </Button>
          </Grid>
        </Grid>

        {/* Convert Time Input */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Time"
              variant="outlined"
              fullWidth
              value={timeInput}
              onChange={handleTimeInputChange}
              onBlur={handleTimeInputBlur}
              placeholder="0:00:00"
              InputProps={{
                sx: {
                  fontFamily: 'monospace',
                  fontSize: '1.1rem',
                  letterSpacing: '0.05em',
                  height: '32px',
                },
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '32px',
                marginBottom: '5px',
                padding: '0 16px',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                width: '100%',
              }}>
              <Typography variant="body2" color="text.secondary">
                Paragraph:{' '}
                <strong>{convertResult.paragraphOrder || '-'}</strong>
                {' | '}
                Sentence: <strong>{convertResult.sentenceOrder || '-'}</strong>
              </Typography>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<SearchIcon />}
              onClick={handleConvert}
              disabled={loading}
              sx={{
                textTransform: 'none',
                height: '32px',
                marginBottom: '5px',
                padding: '0 16px',
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                },
              }}>
              Find
            </Button>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider sx={{ my: 3 }} />

        {/* Three Panel Layout */}
        <Box sx={{ display: 'flex', height: '600px', gap: 2 }}>
          {/* Left Panel - Paragraphs List */}
          <Box
            sx={{
              width: '25%',
              borderRight: '1px solid #e0e0e0',
              pr: 2,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}>
              <Typography variant="h6">Paragraphs</Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleGenerateChapterAudio}
                disabled={loading || paragraphs.length === 0}>
                Generate Chapter Audio
              </Button>
            </Box>
            <Box sx={{ height: '420px', overflow: 'auto', flex: 1 }}>
              {paragraphs.map((paragraph, index) => (
                <Box
                  key={paragraph.id}
                  onClick={() => {
                    setSelectedParagraph(index)
                    setSelectedSentence(0)
                  }}
                  sx={{
                    p: 2,
                    mb: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor:
                      selectedParagraph === index ? '#e3f2fd' : 'white',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight:
                        selectedParagraph === index ? 'bold' : 'normal',
                    }}>
                    Paragraph {paragraph.orderIndex}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Fixed Chapter Audio Player at bottom of Paragraphs */}
            <CustomAudioField
              label="Chapter Audio"
              loading={isEmpty(
                getVoiceAudioPath(chapter?.voices, selectedVoice),
              )}
              audioPath={getVoiceAudioPath(chapter?.voices, selectedVoice)}
            />
          </Box>

          {/* Middle Panel - Sentences List */}
          <Box
            sx={{
              width: '35%',
              borderRight: '1px solid #e0e0e0',
              pr: 2,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}>
              <Typography variant="h6">Sentences</Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleGenerateParagraphAudio}
                disabled={loading}>
                Generate Paragraph Audio
              </Button>
            </Box>
            <Box sx={{ height: '420px', overflow: 'auto', flex: 1 }}>
              {sentencesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : sentences.length > 0 ? (
                sentences.map((sentence, index) => (
                  <Box
                    key={sentence.id}
                    onClick={() => setSelectedSentence(index)}
                    sx={{
                      p: 2,
                      mb: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      cursor: 'pointer',
                      backgroundColor:
                        selectedSentence === index ? '#e8f5e8' : 'white',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      position: 'relative',
                    }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight:
                          selectedSentence === index ? 'bold' : 'normal',
                      }}>
                      Sentence {sentence.orderIndex}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1 }}>
                      {sentence.content?.substring(0, 50)}...
                    </Typography>
                    <Typography
                      variant="caption"
                      color={
                        sentence.status === 'completed'
                          ? 'success.main'
                          : 'warning.main'
                      }>
                      {sentence.status}
                    </Typography>
                    {sentence.qcStatus && (
                      <Chip
                        label={`${sentence.qcAccuracyPercentage}%`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 10,
                          right: 10,
                          backgroundColor:
                            sentence.qcStatus === 'approved' ? 'green' : 'red',
                          color: 'white',
                          fontSize: '0.65rem',
                          height: '20px',
                        }}
                      />
                    )}
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No sentences available
                </Typography>
              )}
            </Box>

            {/* Fixed Audio Player at bottom of Sentences */}
            <CustomAudioField
              label="Paragraph Audio"
              loading={isEmpty(
                getVoiceAudioPath(
                  paragraphs[selectedParagraph]?.voices,
                  selectedVoice,
                ),
              )}
              audioPath={getVoiceAudioPath(
                paragraphs[selectedParagraph]?.voices,
                selectedVoice,
              )}
            />
          </Box>

          {/* Right Panel - Sentence Details */}
          <Box sx={{ width: '40%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}>
              <Box>
                <Typography variant="h6">Sentence Details</Typography>
                {selectedVoice && (
                  <Typography variant="caption" color="text.secondary">
                    Voice: {selectedVoice}
                  </Typography>
                )}
              </Box>
              <IconButton
                onClick={handleReloadSentence}
                title="Reload sentence data"
                size="small">
                <RefreshIcon />
              </IconButton>
            </Box>
            {sentenceFormData ? (
              <Box sx={{ height: '520px', overflow: 'auto' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Sentence Text"
                      multiline
                      rows={4}
                      variant="outlined"
                      fullWidth
                      value={sentenceFormData.content}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Sentence Text With Breaks"
                      multiline
                      rows={4}
                      variant="outlined"
                      fullWidth
                      value={sentenceFormData.contentWithBreaks}
                      onChange={e =>
                        handleSentenceChange(
                          'contentWithBreaks',
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Diff with STT Content{' '}
                      {selectedVoice && `(${selectedVoice})`}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        p: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: '#f9f9f9',
                        minHeight: '60px',
                      }}>
                      {sentenceFormData?.qcDiffResult?.length > 0 ? (
                        sentenceFormData.qcDiffResult.map((item, index) => (
                          <span
                            key={index}
                            style={{
                              color:
                                item.type === 'removed'
                                  ? 'red'
                                  : item.type === 'added'
                                    ? 'green'
                                    : 'black',
                            }}>
                            {item.value + ' '}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: '#666', fontStyle: 'italic' }}>
                          No STT diff data available for this voice
                        </span>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={`Speak Rate ${selectedVoice ? `(${selectedVoice})` : ''}`}
                      variant="outlined"
                      fullWidth
                      value={sentenceFormData.speakRate}
                      onChange={e =>
                        handleSentenceChange('speakRate', e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={`QC Accuracy Percentage ${selectedVoice ? `(${selectedVoice})` : ''}`}
                      variant="outlined"
                      fullWidth
                      value={sentenceFormData?.qcAccuracyPercentage}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2" gutterBottom>
                      Status {selectedVoice && `(${selectedVoice})`}
                    </Typography>
                    <Chip
                      label={sentenceFormData.status}
                      color={
                        sentenceFormData.status === 'completed'
                          ? 'success'
                          : 'warning'
                      }
                      variant="outlined"
                    />
                  </Grid>

                  {sentenceFormData?.audioPath && (
                    <Grid item xs={9}>
                      <CustomAudioField
                        label={`Sentence Audio ${selectedVoice ? `(${selectedVoice})` : ''}`}
                        loading={isEmpty(sentenceFormData?.audioPath)}
                        audioPath={sentenceFormData?.audioPath}
                        inline={true}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box
                      sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpdateSentenceAndTriggerTTS}
                        disabled={loading}>
                        Update Sentence And Trigger TTS
                      </Button>
                      {/*<Button*/}
                      {/*  variant="contained"*/}
                      {/*  color="success"*/}
                      {/*  onClick={() => {*/}
                      {/*    // Mark as QC Done*/}
                      {/*    handleSentenceChange('status', 'completed')*/}
                      {/*    console.log(*/}
                      {/*      'QC Done for sentence:',*/}
                      {/*      sentenceFormData.text,*/}
                      {/*    )*/}
                      {/*  }}*/}
                      {/*  disabled={loading}>*/}
                      {/*  QC Done*/}
                      {/*</Button>*/}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a sentence to view details
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

const ChaptersTabManager = () => {
  const record = useRecordContext()
  const [activeTab, setActiveTab] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [chapterDetails, setChapterDetails] = React.useState({}) // Store full chapter details by chapter ID
  const [loadingChapterDetails, setLoadingChapterDetails] = React.useState({}) // Track loading state for each chapter
  const [selectedVoice, setSelectedVoice] = React.useState(record?.voice || '') // Voice selection state
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()

  // Get chapters from the new data structure (id|orderIndex|status|title only)
  const chapters = record?.chapters || []

  // Update selected voice when record changes
  React.useEffect(() => {
    if (record?.voice) {
      setSelectedVoice(record.voice)
    } else if (record?.voices && record.voices.length > 0 && !selectedVoice) {
      // Set default to first voice if no voice is selected
      setSelectedVoice(record.voices[0])
    }
  }, [record?.voice, record?.voices, selectedVoice])

  // Function to fetch full chapter details from ai-book-chapters API
  const fetchChapterDetails = async chapterId => {
    if (chapterDetails[chapterId] || loadingChapterDetails[chapterId]) {
      return chapterDetails[chapterId] // Return cached data or skip if already loading
    }

    try {
      setLoadingChapterDetails(prev => ({ ...prev, [chapterId]: true }))

      const response = await dataProvider.getOne('ai-book-chapters', {
        id: chapterId,
        bookId: record.id,
      })

      if (response.data) {
        setChapterDetails(prev => ({
          ...prev,
          [chapterId]: response.data,
        }))
        return response.data
      }
    } catch (error) {
      console.error('Error fetching chapter details:', error)
      notify('Error loading chapter details', { type: 'error' })
    } finally {
      setLoadingChapterDetails(prev => ({ ...prev, [chapterId]: false }))
    }
  }

  const getTabColor = status => {
    switch (status) {
      case 'error':
        return '#f44336' // red
      case 'completed':
        return '#4caf50' // green
      default:
        return '#ff9800' // yellow
    }
  }

  // Hàm để lấy màu cho mỗi voice
  const getVoiceColor = (voice, index) => {
    // Màu cố định cho giọng nam và nữ
    if (voice === 'vi-VN-Chirp3-HD-Algenib' || voice === 'vi-VN-Chirp3-HD-Umbriel' || voice === 'vi-VN-Chirp3-HD-Puck' || voice === 'vi-VN-Chirp3-HD-Enceladus') {
      return '#1976d2' // xanh dương cho giọng nam
    }
    if (voice === 'vi-VN-Chirp3-HD-Callirrhoe') {
      return '#e91e63' // hồng cho giọng nữ
    }
    if (voice === 'vi-VN-Chirp3-HD-Gacrux') {
      return '#e91e63' // xanh lá cho giọng nam
    }
    return '#ff9800' // yellow
  }

  const handleTabChange = async (event, newValue) => {
    setActiveTab(newValue)

    // Fetch chapter details when tab is clicked
    if (chapters[newValue] && chapters[newValue].id) {
      await fetchChapterDetails(chapters[newValue].id)
    }
  }

  // Fetch details for the first chapter on component mount
  React.useEffect(() => {
    if (chapters.length > 0 && chapters[0].id) {
      fetchChapterDetails(chapters[0].id)
    }
  }, [chapters])

  const handleSaveChapter = async (chapterId, payload) => {
    setLoading(true)
    try {
      await dataProvider.update('ai-books/chapters', {
        id: chapterId,
        data: payload,
      })
      notify('Chapter saved successfully', { type: 'success' })
      refresh()
    } catch (error) {
      console.error('Error saving chapter:', error)
      notify('Error saving chapter', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleReExtractText = async chapter => {
    try {
      setLoading(true)

      // Get the book record to access pdfPath and other info
      const bookRecord = await dataProvider.getOne('ai-books', {
        id: record.id,
      })

      await dataProvider.create('ai-books/re-extract-text', {
        data: {
          bookId: record.id,
          chapterId: chapter.id,
          pdfPath: bookRecord.data.sourcePdfPath,
          chapterInfo: {
            title: chapter.title,
            orderIndex: chapter.orderIndex,
            startPage: chapter.startPage,
            endPage: chapter.endPage,
          },
        },
      })
      notify('Chapter text re-extraction started', { type: 'success' })
      refresh()
    } catch (error) {
      console.error('Error re-extracting chapter text:', error)
      notify('Error re-extracting chapter text', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleTriggerTTS = async payload => {
    setLoading(true)
    try {
      await dataProvider.create('ai-books/trigger-tts', {
        data: payload,
      })
      notify('TTS triggered successfully', { type: 'success' })
    } catch (error) {
      console.error('Error triggering TTS:', error)
      notify('Error triggering TTS', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadOutline = async () => {
    if (!record?.id || !selectedVoice) {
      notify('Vui lòng chọn voice để download outline', { type: 'warning' })
      return
    }

    try {
      setLoading(true)
      notify('Đang tải outline.json...', { type: 'info' })

      // Gọi API để lấy outline JSON
      const response = await dataProvider.create('ai-books/get-outline', {
        data: {
          bookId: record.id,
          voice: selectedVoice,
        },
      })

      // Lấy JSON data từ response - xử lý nhiều format
      let jsonData
      if (response.data) {
        // Nếu response có cấu trúc { data: ... }
        jsonData = response.data
        // Nếu data là string, parse nó
        if (typeof jsonData === 'string') {
          try {
            jsonData = JSON.parse(jsonData)
          } catch (e) {
            // Nếu không parse được, dùng string đó
            jsonData = response.data
          }
        }
      } else if (typeof response === 'string') {
        // Nếu response là string trực tiếp
        try {
          jsonData = JSON.parse(response)
        } catch (e) {
          jsonData = response
        }
      } else {
        // Nếu response là object trực tiếp
        jsonData = response
      }

      // Tạo blob từ JSON data
      const jsonString =
        typeof jsonData === 'string'
          ? jsonData
          : JSON.stringify(jsonData, null, 2)
      const blob = new Blob([jsonString], { type: 'application/json' })
      const blobUrl = window.URL.createObjectURL(blob)

      // Tạo link download
      const a = document.createElement('a')
      a.href = blobUrl
      a.style.display = 'none'
      // Tạo tên file với bookId và voice
      const fileName = `outline-${record.id}-${selectedVoice.replace(/\//g, '-')}.json`
      a.setAttribute('download', fileName)
      document.body.appendChild(a)
      a.click()

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a)
        window.URL.revokeObjectURL(blobUrl)
      }, 100)

      notify('Download outline.json thành công', { type: 'success' })
    } catch (error) {
      console.error('Error downloading outline:', error)
      const errorMessage = error?.message || 'Lỗi khi tải outline.json'
      notify(errorMessage, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadForeignWords = async () => {
    if (!record?.id) {
      notify('Book ID không tồn tại', { type: 'warning' })
      return
    }

    try {
      setLoading(true)
      notify('Đang tải foreign words...', { type: 'info' })

      const response = await dataProvider.create('ai-books/get-foreign-words', {
        data: {
          bookId: record.id,
        },
      })

      let jsonData
      if (response.data) {
        jsonData = response.data
        if (typeof jsonData === 'string') {
          try {
            jsonData = JSON.parse(jsonData)
          } catch (e) {
            jsonData = response.data
          }
        }
      } else if (typeof response === 'string') {
        try {
          jsonData = JSON.parse(response)
        } catch (e) {
          jsonData = response
        }
      } else {
        jsonData = response
      }

      const jsonString =
        typeof jsonData === 'string'
          ? jsonData
          : JSON.stringify(jsonData, null, 2)
      const blob = new Blob([jsonString], { type: 'application/json' })
      const blobUrl = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = blobUrl
      a.style.display = 'none'
      const fileName = `foreign-words-${record.id}.json`
      a.setAttribute('download', fileName)
      document.body.appendChild(a)
      a.click()

      setTimeout(() => {
        document.body.removeChild(a)
        window.URL.revokeObjectURL(blobUrl)
      }, 100)

      notify('Download foreign words thành công', { type: 'success' })
    } catch (error) {
      console.error('Error downloading foreign words:', error)
      const errorMessage = error?.message || 'Lỗi khi tải foreign words'
      notify(errorMessage, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (!chapters || chapters.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">No chapters available</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Voice Selection */}
      {record?.voices && record.voices.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Select Voice</InputLabel>
              <Select
                value={selectedVoice}
                label="Select Voice"
                onChange={e => setSelectedVoice(e.target.value)}
                renderValue={value => {
                  const voiceColor = getVoiceColor(value)
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: voiceColor,
                          border: '2px solid',
                          borderColor: voiceColor,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          flexShrink: 0,
                        }}
                      />
                      {value}
                    </Box>
                  )
                }}>
                {record.voices.map((voice, index) => {
                  const voiceColor = getVoiceColor(voice)
                  return (
                    <MenuItem key={voice} value={voice}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: voiceColor,
                            border: '2px solid',
                            borderColor:
                              voice === selectedVoice
                                ? voiceColor
                                : 'rgba(0,0,0,0.23)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            flexShrink: 0,
                          }}
                        />
                        {voice}
                      </Box>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Box>

          {/* Sample Audio Player for Selected Voice */}
          {selectedVoice && record?.sampleAudioFiles && (
            <Box sx={{ mt: 2 }}>
              <CustomAudioField
                label={`Sample Audio - ${selectedVoice}`}
                loading={false}
                audioPath={
                  record.sampleAudioFiles.find(
                    sample => sample.voice === selectedVoice,
                  )?.url || null
                }
                inline={true}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Download Buttons - Above chapter list */}
      {record?.voices && record.voices.length > 0 && (
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadOutline}
            disabled={loading || !selectedVoice || !record?.id}
            sx={{ textTransform: 'none' }}>
            Download Outline JSON
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadForeignWords}
            disabled={loading || !record?.id}
            sx={{ textTransform: 'none' }}>
            Download Foreign Words
          </Button>
        </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="chapter tabs"
          sx={{
            '& .MuiTabs-flexContainer': {
              flexWrap: 'wrap',
              gap: '4px',
              alignItems: 'flex-start',
            },
            '& .MuiTabs-indicator': {
              display: 'none', // Hide the indicator when wrapping
            },
            minHeight: 'auto',
          }}>
          {chapters.map((chapter, index) => (
            <Tab
              key={chapter.id || index}
              label={`Chapter ${chapter.orderIndex || index + 1}`}
              id={`chapter-tab-${index}`}
              aria-controls={`chapter-tabpanel-${index}`}
              sx={{
                color: getTabColor(chapter.status),
                minWidth: 'auto',
                minHeight: '36px',
                padding: '8px 16px',
                margin: '2px',
                borderRadius: '6px',
                border: '1px solid transparent',
                fontSize: '0.875rem',
                textTransform: 'none',
                '&.Mui-selected': {
                  color: 'white',
                  fontWeight: 'bold',
                  backgroundColor: getTabColor(chapter.status),
                  border: `1px solid ${getTabColor(chapter.status)}`,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  transform: 'translateY(-1px)',
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
          ))}
        </Tabs>
      </Box>

      {chapters.map((chapter, index) => (
        <div
          key={chapter.id || index}
          role="tabpanel"
          hidden={activeTab !== index}
          id={`chapter-tabpanel-${index}`}
          aria-labelledby={`chapter-tab-${index}`}>
          {activeTab === index && (
            <Box sx={{ p: 3 }}>
              {loadingChapterDetails[chapter.id] ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px',
                  }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>
                    Loading chapter details...
                  </Typography>
                </Box>
              ) : chapterDetails[chapter.id] ? (
                <ChapterTab
                  chapter={{
                    ...chapter, // Basic info from chapters array (id, orderIndex, status, title)
                    ...chapterDetails[chapter.id], // Full details from API (paragraphs, etc.)
                  }}
                  bookId={record?.id}
                  onSave={handleSaveChapter}
                  onTriggerTTS={handleTriggerTTS}
                  onReExtractText={handleReExtractText}
                  loading={loading}
                  selectedVoice={selectedVoice}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px',
                  }}>
                  <Typography>No chapter details available</Typography>
                </Box>
              )}
            </Box>
          )}
        </div>
      ))}
    </Box>
  )
}

export default ChaptersTabManager
